<template>
	<form ref="form" class="flex flex-col gap-3" :endpoint="endpoint" method="POST" @submit.prevent="onSubmit">
		<slot name="csrf" />
		<slot name="recaptcha" />
		<slot name="error" />
		<input type="hidden" name="remember" value="1" />
		<input type="hidden" name="country" v-model="country" />
		<input type="hidden" name="preferred" value="1" />
		<div :class="['relative', error && 'has-error']">
			<input
				type="text"
				id="firstName"
				name="firstName"
				v-model="firstName"
				placeholder="First Name"
				autocomplete="given-name"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="firstName" :class="['seint-floating-label seint-input', error && '!text-rose-600']">First Name</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
		</div>
		<div :class="['relative', error && 'has-error']">
			<input
				type="text"
				id="lastName"
				name="lastName"
				v-model="lastName"
				placeholder="Last Name"
				autocomplete="family-name"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="lastName" :class="['seint-floating-label seint-input', error && '!text-rose-600']">Last Name</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
		</div>
		<div :class="['relative', error && 'has-error']">
			<input
				type="text"
				id="username"
				name="username"
				v-model="username"
				placeholder="Username"
				autocomplete="username"
				spellcheck="false"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="username" :class="['seint-floating-label seint-input', error && '!text-rose-600']">Username</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
		</div>
		<div :class="['relative', error && 'has-error']">
			<input
				type="email"
				id="email"
				name="email"
				v-model="email"
				placeholder="Email"
				autocomplete="email"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="email" :class="['seint-floating-label seint-input', error && '!text-rose-600']">Email</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
		</div>
		<!-- <span class="seint-note text-left mt-0">We'll send you a code to verify your email</span> -->
		<div :class="['relative', error && 'has-error']">
			<input
				type="tel"
				id="phone"
				name="phone"
				v-model="phone"
				required
				v-on:blur="handlePhoneCheck"
				v-mask="phoneMask"
				placeholder="Phone"
				autocomplete="tel"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="phone" :class="['seint-floating-label seint-input', error && '!text-rose-600']">Phone</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
		</div>
		<span v-if="showPhoneNote" class="seint-note text-left mt-0 text-rose-600">
			Phone has format: {{ phoneMask[0] }}
		</span>
		<!-- <span v-if="!showPhoneNote" class="seint-note text-left mt-0">We'll send you a code to verify your phone</span> -->
		<div :class="['relative', error && 'has-error']">
			<input
				:type="showPassword ? 'text' : 'password'"
				id="password"
				name="password"
				v-model="password"
				placeholder="Password"
				autocomplete="new-password"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="password" :class="['seint-floating-label seint-input', error && '!text-rose-600']">Password</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
			<button
				type="button"
				aria-label="Show Password"
				@click="handlePasswordVisibility"
				:class="['absolute top-2/4 right-3 -translate-y-2/4 block', showPassword && '!hidden']"
			>
				<svg-vue icon="eye" />
			</button>
			<button
				type="button"
				aria-label="Show Password"
				@click="handlePasswordVisibility"
				:class="['absolute top-2/4 right-3 -translate-y-2/4 hidden', showPassword && '!block']"
			>
				<svg-vue icon="eye-slash" />
			</button>
		</div>
		<div :class="['relative', error || (passwordConfirmationError && 'has-error')]">
			<input
				:type="showPasswordConfirmation ? 'text' : 'password'"
				id="passwordConfirmation"
				name="password_confirmation"
				v-model="passwordConfirmation"
				placeholder="Confirm Password"
				autocomplete="new-password"
				:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
			/>
			<label for="passwordConfirmation" :class="['seint-floating-label seint-input', error && '!text-rose-600']">
				Confirm Password
			</label>
			<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
			<button
				v-if="!error"
				type="button"
				tabindex="-1"
				aria-label="Show Password"
				@click="handlePasswordConfirmationVisibility"
				:class="['absolute top-2/4 right-3 -translate-y-2/4 block', showPasswordConfirmation && '!hidden']"
			>
				<svg-vue icon="eye" />
			</button>
			<button
				v-if="!error"
				type="button"
				tabindex="-1"
				aria-label="Show Password"
				@click="handlePasswordConfirmationVisibility"
				:class="['absolute top-2/4 right-3 -translate-y-2/4 hidden', showPasswordConfirmation && '!block']"
			>
				<svg-vue icon="eye-slash" />
			</button>
		</div>
		<span v-if="passwordConfirmationError" class="seint-note text-left mt-0 text-rose-600">
			Password does not match
		</span>
		<span class="seint-note text-left mt-0">
			Must be at least 8 characters and contain special character and number
		</span>
		<p v-show="error" class="alert alert-danger p-1 mb-0 mt-3">{{ error }}</p>
		<input type="hidden" name="recaptchaToken" :value="recaptchaToken" />
		<slot name="actions">
			<button type="submit" class="seint-btn" :disabled="isDisabled || loading" :aria-label="btnTxt">
				<loader v-if="loading" /><span v-else>{{ btnTxt }}</span>
			</button>
		</slot>
		<generic-modal ref="emailVerifyModal" title="Email Verification">
			<template v-slot:content>
				<form :endpoint="verifyEmailEndpoint" method="POST" @submit.prevent="handleVerifyEmail">
					<div :class="['relative', verificationError && 'has-error']">
						<input
							type="tel"
							id="code"
							name="code"
							v-model="verificationCode"
							placeholder="Verification Code"
							autocomplete="one-time-code"
							:class="['peer seint-floating-input seint-input', error && '!border-rose-400']"
						/>
						<label for="code" :class="['seint-floating-label seint-input', error && '!text-rose-600']">
							Verification Code
						</label>
						<svg-vue icon="alert" :class="['seint-floating-svg', error && 'block']" />
					</div>
					<span class="seint-note">We sent a code to {{ email }}</span>
					<button
						type="submit"
						class="seint-btn"
						:disabled="isDisabled || loading"
						aria-label="Confirm"
						@click.prevent="onSubmit"
					>
						<loader v-if="loading" /><span v-else>Confirm</span>
					</button>
				</form>
			</template>
		</generic-modal>
	</form>
</template>
<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'

const props = defineProps({
	endpoint: {
		type: String,
		required: true,
	},
	currentMarket: {
		type: String,
		required: true,
	},
	verifyEmailEndpoint: {
		type: String,
		required: false,
	},
	customerSite: {
		type: Object,
		required: false,
	},
	masterAccount: {
		type: Number,
		required: false,
	},
	initialFirstName: {
		type: String,
		default: '',
	},
	initialLastName: {
		type: String,
		default: '',
	},
	initialUsername: {
		type: String,
		default: '',
	},
	initialEmail: {
		type: String,
		default: '',
	},
	initialPhone: {
		required: false,
		default: '',
	},
	initialCountry: {
		type: String,
		default: null,
	},
	buttonId: {
		type: String,
		required: false,
	},
})

const RECAPTCHA_API_SITE_KEY = import.meta.env.VITE_RECAPTCHA_API_SITE_KEY
onMounted(() => {
	const recaptchaScript = document.createElement('script')
	recaptchaScript.setAttribute('src', `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_API_SITE_KEY}`)
	document.head.appendChild(recaptchaScript)
})

const loading = ref(false)
const error = ref(null)
const form = ref(null)
const recaptchaToken = ref(null)
const firstName = ref(props.initialFirstName)
const lastName = ref(props.initialLastName)
const username = ref(props.initialUsername)
const email = ref(props.initialEmail)
const phone = ref(props.initialPhone)
const showPhoneNote = ref(false)
const country = ref(props.initialCountry ?? props.currentMarket)
const verificationCode = ref('')
const verificationError = ref(null)
const password = ref('')
const showPassword = ref(false)
const passwordConfirmation = ref('')
const showPasswordConfirmation = ref(false)

const phoneLength = computed(() => {
	if (country.value === 'US') {
		return '+1 ###-###-####'.length
	}
	if (country.value === 'CA') {
		return '+1 ###-###-####'.length
	}
	if (['UK', 'GB'].includes(country.value)) {
		return '+44 ## #### ####'.length
	}
})
const handlePhoneCheck = () => {
	if (phone.value.length > 0 && phone.value.length < phoneLength.value) {
		// phone value invalid
		showPhoneNote.value = true
		return
	}
	showPhoneNote.value = false
}
const passwordConfirmationError = computed(() => {
	return passwordConfirmation.value !== password.value
})
const phoneMask = computed(() => {
	if (country.value === 'US') {
		return ['+1 ###-###-####']
	}
	if (country.value === 'CA') {
		return ['+1 ###-###-####']
	}
	if (['UK', 'GB'].includes(country.value)) {
		return ['+44 ## #### ####']
	}
})
const isDisabled = computed(() => {
	const firstNameEmpty = firstName.value?.trim().length === 0
	const lastNameEmpty = lastName.value?.trim().length === 0
	const usernameEmpty = username.value?.trim().length === 0
	const emailEmpty = email.value?.trim().length === 0
	const phoneEmpty = phone.value?.trim().length === 0 && !['UK', 'GB'].includes(country.value)
	const phoneToShort = phone.value?.trim().length < 15 && !['UK', 'GB'].includes(country.value)
	const passwordEmpty = password.value?.trim().length === 0
	const passwordConfirmationEmpty = passwordConfirmation.value?.trim().length === 0
	const passwordsNotMatch = passwordConfirmation.value !== password.value

	return [
		firstNameEmpty,
		lastNameEmpty,
		usernameEmpty,
		emailEmpty,
		phoneEmpty,
		phoneToShort,
		passwordEmpty,
		passwordConfirmationEmpty,
		passwordsNotMatch,
	].some(Boolean)
})
const btnTxt = computed(() => {
	if (props.customerSite) {
		const { CustomerID, FirstName, LastName } = props.customerSite
		return CustomerID === props.masterAccount ? 'Shop without an Artist' : `Shop with ${FirstName} ${LastName}`
	}
	return 'Submit'
})

watch(isDisabled, (newVal) => {
	if (props.buttonId) {
		document.getElementById(props.buttonId).disabled = newVal
	}
})
const handlePasswordVisibility = () => {
	showPassword.value = !showPassword.value
}
const handlePasswordConfirmationVisibility = () => {
	showPasswordConfirmation.value = !showPasswordConfirmation.value
}
const onSubmit = () => {
	loading.value = true
	if (grecaptcha) {
		return grecaptcha.enterprise.ready(async () => {
			const token = await grecaptcha.enterprise.execute(RECAPTCHA_API_SITE_KEY, { action: 'LOGIN' })
			recaptchaToken.value = token
			nextTick(() => form.value.submit())
		})
	}
	nextTick(() => form.value.submit())
}
</script>
